import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { authService } from './services/auth';
import Layout from './components/Layout';
import Login from './pages/Login';
import AdminPanel from './pages/AdminPanel';
import Chat from './pages/Chat';
import './index.css';

// Protected Route Component
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  requiredRole?: 'user' | 'staff' | 'superadmin';
}> = ({
  children,
  requiredRole
}) => {
  const isAuthenticated = authService.isAuthenticated();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole === 'superadmin' && !authService.isSuperAdmin()) {
    // Faculty still have a home inside /admin — send them there rather than
    // bouncing them all the way out to the chat page.
    return <Navigate to={authService.isFaculty() ? '/admin/documents' : '/chat'} replace />;
  }

  if (requiredRole === 'staff' && !authService.isStaff()) {
    return <Navigate to="/chat" replace />;
  }

  if (requiredRole === 'user' && !authService.isUser()) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component (redirect if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();

  if (isAuthenticated) {
    if (authService.isSuperAdmin()) {
      return <Navigate to="/admin/dashboard" replace />;
    } else if (authService.isFaculty()) {
      return <Navigate to="/admin/documents" replace />;
    } else {
      return <Navigate to="/chat" replace />;
    }
  }

  return <>{children}</>;
};

// Every admin section renders the same shell — the drawer route decides which
// section AdminPanel shows. Two gates: superadmin-only sections use
// `adminElement`, the faculty-reachable Documents section uses `staffElement`.
const adminElement = (
  <ProtectedRoute requiredRole="superadmin">
    <AdminPanel />
  </ProtectedRoute>
);

const staffElement = (
  <ProtectedRoute requiredRole="staff">
    <AdminPanel />
  </ProtectedRoute>
);

function App() {
  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          
          {/* Protected Routes */}
          <Route
            path="/admin"
            element={<Navigate to="/admin/dashboard" replace />}
          />
          <Route path="/admin/dashboard" element={adminElement} />
          <Route path="/admin/documents" element={staffElement} />
          <Route path="/admin/students" element={adminElement} />
          <Route path="/admin/staff" element={adminElement} />
          <Route path="/admin/chats" element={adminElement} />

          <Route 
            path="/chat" 
            element={
              <ProtectedRoute requiredRole="user">
                <Chat />
              </ProtectedRoute>
            } 
          />
          
          {/* Default Route */}
          <Route 
            path="/" 
            element={<Navigate to="/login" replace />} 
          />
          
          {/* Catch all route */}
          <Route 
            path="*" 
            element={<Navigate to="/login" replace />} 
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
